(() => {
  'use strict';

  document
    .querySelectorAll('[data-cce13]')
    .forEach((root) => {

      if (root.dataset.ready === 'true') {
        return;
      }

      root.dataset.ready = 'true';


      /* =========================================================
         CONFIG
      ========================================================== */

      const currency =
        root.dataset.currency ||
        window.Shopify?.currency?.active ||
        'USD';

      const checkoutUrl =
        root.dataset.checkoutUrl ||
        '/checkout';

      const discountCode =
        String(
          root.dataset.discountCode ||
          ''
        ).trim();

      const discountMin =
        Math.max(
          1,
          Number(
            root.dataset.discountMin ||
            3
          )
        );

      const discountPercent =
        Math.max(
          0,
          Number(
            root.dataset.discountPercent ||
            10
          )
        );


      let currentCart = null;

      let syncingCart = false;

      let builderMainActive = true;


      /* =========================================================
         HELPERS
      ========================================================== */

      function shopRoot() {
        return window.Shopify?.routes?.root || '/';
      }


      function clampQty(value) {

        const parsed =
          parseInt(
            value,
            10
          );

        if (Number.isNaN(parsed)) {
          return 1;
        }

        return Math.max(
          1,
          Math.min(
            99,
            parsed
          )
        );

      }


      function normalize(value) {

        return String(
          value ||
          ''
        )
          .toLowerCase()
          .trim()
          .replace(/\s+/g, ' ');

      }


      function formatMoney(cents) {

        try {

          return new Intl.NumberFormat(
            document.documentElement.lang ||
            'en',
            {
              style: 'currency',
              currency
            }
          ).format(
            Number(cents || 0) /
            100
          );

        } catch (error) {

          return (
            Number(cents || 0) /
            100
          ).toFixed(2);

        }

      }


      function animateUpdate(element) {

        if (!element) return;

        element.classList.remove(
          'is-updated'
        );

        void element.offsetWidth;

        element.classList.add(
          'is-updated'
        );

        window.setTimeout(
          () => {

            element.classList.remove(
              'is-updated'
            );

          },
          380
        );

      }


      /* =========================================================
         REVEAL ANIMATION
      ========================================================== */

      const revealElements =
        Array.from(
          root.querySelectorAll(
            '[data-reveal]'
          )
        );


      if ('IntersectionObserver' in window) {

        const observer =
          new IntersectionObserver(
            (entries) => {

              entries.forEach(
                (entry) => {

                  if (!entry.isIntersecting) {
                    return;
                  }

                  entry.target.classList.add(
                    'is-revealed'
                  );

                  observer.unobserve(
                    entry.target
                  );

                }
              );

            },
            {
              threshold: .06
            }
          );


        revealElements.forEach(
          (element) => {

            observer.observe(
              element
            );

          }
        );

      } else {

        revealElements.forEach(
          (element) => {

            element.classList.add(
              'is-revealed'
            );

          }
        );

      }


      /* =========================================================
         TOAST
      ========================================================== */

      const toast =
        root.querySelector(
          '[data-toast]'
        );


      function showToast(message) {

        if (!toast) return;

        toast.textContent =
          message;

        toast.classList.add(
          'is-visible'
        );

        window.clearTimeout(
          showToast.timer
        );

        showToast.timer =
          window.setTimeout(
            () => {

              toast.classList.remove(
                'is-visible'
              );

            },
            2100
          );

      }


      /* =========================================================
         PRODUCT REGISTRIES
      ========================================================== */

      function candleVariantIds() {

        return new Set(
          Array
            .from(
              root.querySelectorAll(
                '[data-candle-eligible][data-variant-id]'
              )
            )
            .map(
              (element) =>
                Number(
                  element.dataset.variantId
                )
            )
            .filter(Boolean)
        );

      }


      function careVariantIds() {

        return new Set(
          Array
            .from(
              root.querySelectorAll(
                '[data-care-eligible][data-variant-id]'
              )
            )
            .map(
              (element) =>
                Number(
                  element.dataset.variantId
                )
            )
            .filter(Boolean)
        );

      }


      /* =========================================================
         HEADER CART COUNT
      ========================================================== */

      function updateThemeCartCount(count) {

        const safeCount =
          Math.max(
            0,
            Number(
              count ||
              0
            )
          );


        const selectors = [
          '[data-cart-count]',
          '.cart-count',
          '[data-cart-count-bubble]',
          '.cart-count-bubble',
          '.cart-count-bubble span[aria-hidden="true"]',
          '[data-testid="cart-count"]'
        ];


        document
          .querySelectorAll(
            selectors.join(',')
          )
          .forEach(
            (element) => {

              if (
                element.children.length &&
                !element.matches('span')
              ) {

                const child =
                  element.querySelector(
                    'span[aria-hidden="true"], span'
                  );

                if (child) {

                  child.textContent =
                    safeCount;

                  return;

                }

              }

              element.textContent =
                safeCount;

            }
          );

      }


      /* =========================================================
         CART API
      ========================================================== */

      async function fetchCart() {

        const response =
          await fetch(
            `${shopRoot()}cart.js`,
            {
              method: 'GET',

              credentials:
                'same-origin',

              headers: {
                Accept:
                  'application/json'
              },

              cache:
                'no-store'
            }
          );


        if (!response.ok) {

          throw new Error(
            'Unable to load your cart.'
          );

        }

        return response.json();

      }


      function mergeLines(lines) {

        const map =
          new Map();


        lines.forEach(
          (line) => {

            const id =
              Number(
                line.id
              );

            const quantity =
              Number(
                line.quantity
              );


            if (
              !id ||
              quantity <= 0
            ) {
              return;
            }


            map.set(
              id,
              (
                map.get(id) ||
                0
              ) +
              quantity
            );

          }
        );


        return Array
          .from(
            map.entries()
          )
          .map(
            ([
              id,
              quantity
            ]) => ({
              id,
              quantity
            })
          );

      }


      async function addLinesToShopify(lines) {

        const items =
          mergeLines(
            lines
          );


        if (!items.length) {

          throw new Error(
            'Choose at least one item.'
          );

        }


        const response =
          await fetch(
            `${shopRoot()}cart/add.js`,
            {
              method:
                'POST',

              credentials:
                'same-origin',

              headers: {
                'Content-Type':
                  'application/json',

                Accept:
                  'application/json',

                'X-Requested-With':
                  'XMLHttpRequest'
              },

              body:
                JSON.stringify({
                  items
                })
            }
          );


        if (!response.ok) {

          const data =
            await response
              .json()
              .catch(
                () => ({})
              );


          throw new Error(
            data.description ||
            data.message ||
            'Unable to add your selection.'
          );

        }


        return response.json();

      }


      async function changeCartLine(
        key,
        quantity
      ) {

        const response =
          await fetch(
            `${shopRoot()}cart/change.js`,
            {
              method:
                'POST',

              credentials:
                'same-origin',

              headers: {
                'Content-Type':
                  'application/json',

                Accept:
                  'application/json'
              },

              body:
                JSON.stringify({
                  id:
                    key,

                  quantity:
                    Math.max(
                      0,
                      Number(
                        quantity
                      )
                    )
                })
            }
          );


        if (!response.ok) {

          throw new Error(
            'Unable to update your cart.'
          );

        }


        return response.json();

      }


      /* =========================================================
         CART SUMMARY
      ========================================================== */

      function getCartSummary(cart) {

        const candleIds =
          candleVariantIds();

        const careIds =
          careVariantIds();


        let candleQty =
          0;

        let candleOriginalSubtotal =
          0;

        let candleFinalSubtotal =
          0;

        let careQty =
          0;

        let careSubtotal =
          0;


        const items = [];


        if (
          !cart ||
          !Array.isArray(
            cart.items
          )
        ) {

          return {
            candleQty: 0,
            candleOriginalSubtotal: 0,
            candleFinalSubtotal: 0,
            careQty: 0,
            careSubtotal: 0,
            actualSaving: 0,
            expectedSaving: 0,
            displaySaving: 0,
            total: 0,
            relevantQty: 0,
            unlocked: false,
            items: []
          };

        }


        cart.items.forEach(
          (item) => {

            const variantId =
              Number(
                item.variant_id
              );


            const originalLine =
              Number(
                item.original_line_price ??
                item.line_price ??
                0
              );


            const finalLine =
              Number(
                item.final_line_price ??
                item.line_price ??
                originalLine
              );


            if (
              candleIds.has(
                variantId
              )
            ) {

              candleQty +=
                Number(
                  item.quantity ||
                  0
                );


              candleOriginalSubtotal +=
                originalLine;


              candleFinalSubtotal +=
                finalLine;


              items.push({
                ...item,

                cceType:
                  'candle',

                cceFinalLine:
                  finalLine
              });


              return;

            }


            if (
              careIds.has(
                variantId
              )
            ) {

              careQty +=
                Number(
                  item.quantity ||
                  0
                );


              careSubtotal +=
                finalLine;


              items.push({
                ...item,

                cceType:
                  'care',

                cceFinalLine:
                  finalLine
              });

            }

          }
        );


        const unlocked =
          candleQty >=
          discountMin;


        const actualSaving =
          Math.max(
            0,
            candleOriginalSubtotal -
            candleFinalSubtotal
          );


        const expectedSaving =
          unlocked
            ? Math.round(
                candleOriginalSubtotal *
                (
                  discountPercent /
                  100
                )
              )
            : 0;


        const displaySaving =
          unlocked
            ? (
                actualSaving > 0
                  ? actualSaving
                  : expectedSaving
              )
            : 0;


        return {
          candleQty,
          candleOriginalSubtotal,
          candleFinalSubtotal,
          careQty,
          careSubtotal,
          actualSaving,
          expectedSaving,
          displaySaving,

          total:
            Math.max(
              0,
              candleOriginalSubtotal -
              displaySaving
            ) +
            careSubtotal,

          relevantQty:
            candleQty +
            careQty,

          unlocked,
          items
        };

      }


      /* =========================================================
         DISCOUNT RECONCILIATION
      ========================================================== */

      async function reconcileAfterAdd(
        shouldUnlock
      ) {

        if (
          shouldUnlock &&
          discountCode
        ) {

          try {

            const response =
              await fetch(
                `${shopRoot()}cart/update.js`,
                {
                  method:
                    'POST',

                  credentials:
                    'same-origin',

                  headers: {
                    'Content-Type':
                      'application/json',

                    Accept:
                      'application/json'
                  },

                  body:
                    JSON.stringify({
                      discount:
                        discountCode
                    })
                }
              );


            if (response.ok) {

              return response.json();

            }

          } catch (error) {

            console.warn(
              '[CCE13 Discount]',
              error
            );

          }

        }


        return fetchCart();

      }


      async function reconcileChangedCart(cart) {

        const summary =
          getCartSummary(
            cart
          );


        if (
          summary.unlocked &&
          discountCode &&
          summary.actualSaving <= 0
        ) {

          try {

            const response =
              await fetch(
                `${shopRoot()}cart/update.js`,
                {
                  method:
                    'POST',

                  credentials:
                    'same-origin',

                  headers: {
                    'Content-Type':
                      'application/json',

                    Accept:
                      'application/json'
                  },

                  body:
                    JSON.stringify({
                      discount:
                        discountCode
                    })
                }
              );


            if (response.ok) {

              return response.json();

            }

          } catch (error) {

            console.warn(
              '[CCE13 Discount Sync]',
              error
            );

          }

        }


        return cart;

      }


      /* =========================================================
         THEME EVENTS
      ========================================================== */

      function broadcastConfirmedCart(cart) {

        if (!cart) return;


        updateThemeCartCount(
          cart.item_count
        );


        const detail = {
          cart,
          source:
            'cce-13oz-metal'
        };


        document.dispatchEvent(
          new CustomEvent(
            'cart:update',
            {
              bubbles: true,
              detail
            }
          )
        );


        document.dispatchEvent(
          new CustomEvent(
            'cart:refresh',
            {
              bubbles: true,
              detail
            }
          )
        );


        window.dispatchEvent(
          new CustomEvent(
            'cart:updated',
            {
              detail
            }
          )
        );

      }


      function optimisticHeaderCount(quantity) {

        updateThemeCartCount(
          Number(
            currentCart?.item_count ||
            0
          ) +
          Number(
            quantity ||
            0
          )
        );

      }


      /* =========================================================
         MAIN PRODUCT
      ========================================================== */

      const mainForm =
        root.querySelector(
          '[data-main-form]'
        );

      const variantSelect =
        root.querySelector(
          '[data-main-variant]'
        );

      const mainQty =
        root.querySelector(
          '[data-main-qty]'
        );

      const mainMinus =
        root.querySelector(
          '[data-main-minus]'
        );

      const mainPlus =
        root.querySelector(
          '[data-main-plus]'
        );

      const mainAdd =
        root.querySelector(
          '[data-main-add]'
        );

      const mainBuy =
        root.querySelector(
          '[data-main-buy]'
        );

      const mainPrice =
        root.querySelector(
          '[data-main-price]'
        );

      const mobileMainPrice =
        root.querySelector(
          '[data-mobile-main-price]'
        );

      const mainSubtotal =
        root.querySelector(
          '[data-main-subtotal]'
        );

      const projectedCount =
        root.querySelector(
          '[data-projected-count]'
        );

      const projectedSaving =
        root.querySelector(
          '[data-projected-saving]'
        );

      const projectedTotal =
        root.querySelector(
          '[data-projected-total]'
        );

      const offerBox =
        root.querySelector(
          '[data-offer-box]'
        );

      const offerMessage =
        root.querySelector(
          '[data-offer-message]'
        );

      const offerCount =
        root.querySelector(
          '[data-offer-count]'
        );

      const offerProgress =
        root.querySelector(
          '[data-offer-progress]'
        );


      function selectedVariant() {

        if (!variantSelect) {

          return {
            id: 0,
            price: 0,
            available: false,
            image: ''
          };

        }


        const option =
          variantSelect.options[
            variantSelect.selectedIndex
          ];


        return {
          id:
            Number(
              option?.value ||
              0
            ),

          price:
            Number(
              option?.dataset.price ||
              0
            ),

          available:
            option?.dataset.available ===
            'true',

          image:
            option?.dataset.image ||
            ''
        };

      }


      /* =========================================================
         BUILDER
      ========================================================== */

      const builderMainCard =
        root.querySelector(
          '[data-builder-main-card]'
        );

      const builderMainToggle =
        root.querySelector(
          '[data-toggle-builder-main]'
        );

      const builderMainStatus =
        root.querySelector(
          '[data-builder-main-status]'
        );

      const builderMainQty =
        root.querySelector(
          '[data-builder-main-qty]'
        );

      const builderMainMinus =
        root.querySelector(
          '[data-builder-main-minus]'
        );

      const builderMainPlus =
        root.querySelector(
          '[data-builder-main-plus]'
        );

      const builderMainPrice =
        root.querySelector(
          '[data-builder-main-price]'
        );


      const candleChoices =
        Array.from(
          root.querySelectorAll(
            '[data-mix-choice]'
          )
        );


      const careChoices =
        Array.from(
          root.querySelectorAll(
            '[data-care-choice]'
          )
        );


      function setMainQuantity(value) {

        const quantity =
          clampQty(
            value
          );


        if (mainQty) {
          mainQty.value =
            quantity;
        }

        if (builderMainQty) {
          builderMainQty.value =
            quantity;
        }


        return quantity;

      }


      function syncMainVariant() {

        const variant =
          selectedVariant();


        if (builderMainPrice) {

          builderMainPrice.textContent =
            formatMoney(
              variant.price
            );

        }

      }


      function updateBuilderMainState() {

        builderMainCard
          ?.classList
          .toggle(
            'is-removed',
            !builderMainActive
          );


        if (builderMainToggle) {

          builderMainToggle.textContent =
            builderMainActive
              ? 'REMOVE'
              : 'ADD BACK';


          builderMainToggle.classList.toggle(
            'is-add',
            !builderMainActive
          );

        }


        if (builderMainStatus) {

          builderMainStatus.textContent =
            builderMainActive
              ? 'SELECTED'
              : 'REMOVED';

        }

      }


      /* =========================================================
         SELECTED ITEMS
      ========================================================== */

      function selectedCandles() {

        const selections = [];

        const variant =
          selectedVariant();


        const mainQuantity =
          clampQty(
            builderMainQty?.value ||
            mainQty?.value ||
            1
          );


        if (
          builderMainActive &&
          variant.id &&
          variant.available
        ) {

          selections.push({
            key: 'main',
            type: 'candle',
            option: 'main',
            id: variant.id,
            price: variant.price,
            quantity: mainQuantity,

            title:
              root
                .querySelector(
                  '.cce13-mobile-heading h1'
                )
                ?.textContent
                ?.trim() ||
              root
                .querySelector(
                  '.cce13-desktop-heading h1'
                )
                ?.textContent
                ?.trim() ||
              '13oz Metal Candle',

            image:
              variant.image ||
              root
                .querySelector(
                  '.cce13-main-choice__img'
                )
                ?.src ||
              ''
          });

        }


        candleChoices
          .filter(
            (card) =>
              card.classList.contains(
                'is-selected'
              )
          )
          .forEach(
            (card) => {

              selections.push({
                key:
                  `candle:${card.dataset.option}:${card.dataset.variantId}`,

                type:
                  'candle',

                option:
                  card.dataset.option,

                id:
                  Number(
                    card.dataset.variantId
                  ),

                price:
                  Number(
                    card.dataset.price
                  ),

                quantity:
                  clampQty(
                    card
                      .querySelector(
                        '[data-choice-qty]'
                      )
                      ?.value ||
                    1
                  ),

                title:
                  card.dataset.title ||
                  'Candle',

                image:
                  card.dataset.image ||
                  ''
              });

            }
          );


        return selections;

      }


      function selectedCare() {

        return careChoices
          .filter(
            (card) =>
              card.classList.contains(
                'is-selected'
              )
          )
          .map(
            (card) => ({
              key:
                `care:${card.dataset.careKind}:${card.dataset.variantId}`,

              type:
                'care',

              kind:
                card.dataset.careKind,

              id:
                Number(
                  card.dataset.variantId
                ),

              price:
                Number(
                  card.dataset.price
                ),

              quantity:
                clampQty(
                  card
                    .querySelector(
                      '[data-care-qty]'
                    )
                    ?.value ||
                  1
                ),

              title:
                card.dataset.title ||
                'Candle Care',

              image:
                card.dataset.image ||
                ''
            })
          );

      }


      /* =========================================================
         CARD STATE
      ========================================================== */

      function resetCandleCard(card) {

        if (!card) return;


        card.classList.remove(
          'is-selected'
        );


        const state =
          card.querySelector(
            '[data-choice-state]'
          );


        if (state) {
          state.textContent =
            'SELECT';
        }


        const remove =
          card.querySelector(
            '[data-remove-choice]'
          );


        if (remove) {
          remove.hidden =
            true;
        }

      }


      function selectCandleCard(card) {

        if (!card) return;


        const group =
          card.dataset.option;


        candleChoices
          .filter(
            (candidate) =>
              candidate.dataset.option ===
              group
          )
          .forEach(
            resetCandleCard
          );


        card.classList.add(
          'is-selected'
        );


        const state =
          card.querySelector(
            '[data-choice-state]'
          );


        if (state) {
          state.textContent =
            'SELECTED ✓';
        }


        const remove =
          card.querySelector(
            '[data-remove-choice]'
          );


        if (remove) {
          remove.hidden =
            false;
        }

      }


      function resetCareCard(card) {

        if (!card) return;


        card.classList.remove(
          'is-selected'
        );


        const kind =
          card.dataset.careKind;


        const toggle =
          card.querySelector(
            '[data-toggle-care]'
          );


        const remove =
          card.querySelector(
            '[data-remove-care]'
          );


        if (toggle) {

          toggle.classList.remove(
            'is-selected'
          );


          toggle.textContent =
            kind === 'warmer'
              ? 'ADD WARMER'
              : 'ADD WICK TRIMMER';

        }


        if (remove) {
          remove.hidden =
            true;
        }


        const status =
          card
            .closest(
              '[data-care-option]'
            )
            ?.querySelector(
              '[data-care-selected-label]'
            );


        if (status) {

          status.classList.remove(
            'is-selected'
          );

          status.textContent =
            'OPTIONAL';

        }

      }


      function selectCareCard(card) {

        if (!card) return;


        card.classList.add(
          'is-selected'
        );


        const kind =
          card.dataset.careKind;


        const toggle =
          card.querySelector(
            '[data-toggle-care]'
          );


        const remove =
          card.querySelector(
            '[data-remove-care]'
          );


        if (toggle) {

          toggle.classList.add(
            'is-selected'
          );


          toggle.textContent =
            kind === 'warmer'
              ? 'WARMER SELECTED ✓'
              : 'TRIMMER SELECTED ✓';

        }


        if (remove) {
          remove.hidden =
            false;
        }


        const status =
          card
            .closest(
              '[data-care-option]'
            )
            ?.querySelector(
              '[data-care-selected-label]'
            );


        if (status) {

          status.classList.add(
            'is-selected'
          );

          status.textContent =
            'SELECTED ✓';

        }

      }


      /* =========================================================
         MAIN BUILDER TOGGLE
      ========================================================== */

      builderMainToggle
        ?.addEventListener(
          'click',
          () => {

            builderMainActive =
              !builderMainActive;


            updateBuilderMainState();

            updateBuilder();

          }
        );


      /* =========================================================
         CANDLE SELECTION
      ========================================================== */

      root.addEventListener(
        'click',
        (event) => {

          const button =
            event.target.closest(
              '[data-select-mix]'
            );


          if (
            !button ||
            button.disabled
          ) {
            return;
          }


          const card =
            button.closest(
              '[data-mix-choice]'
            );


          if (!card) return;


          if (
            card.classList.contains(
              'is-selected'
            )
          ) {

            resetCandleCard(
              card
            );

          } else {

            selectCandleCard(
              card
            );

          }


          updateBuilder();

        }
      );


      root.addEventListener(
        'click',
        (event) => {

          const remove =
            event.target.closest(
              '[data-remove-choice]'
            );


          if (!remove) return;


          event.preventDefault();
          event.stopPropagation();


          resetCandleCard(
            remove.closest(
              '[data-mix-choice]'
            )
          );


          updateBuilder();


          showToast(
            'Candle removed.'
          );

        }
      );


      /* =========================================================
         CANDLE QUANTITY
      ========================================================== */

      root.addEventListener(
        'click',
        (event) => {

          const plus =
            event.target.closest(
              '[data-choice-plus]'
            );

          const minus =
            event.target.closest(
              '[data-choice-minus]'
            );


          if (
            !plus &&
            !minus
          ) {
            return;
          }


          const card =
            event.target.closest(
              '[data-mix-choice]'
            );


          const input =
            card?.querySelector(
              '[data-choice-qty]'
            );


          if (!input) return;


          let quantity =
            clampQty(
              input.value
            );


          quantity =
            plus
              ? Math.min(
                  99,
                  quantity + 1
                )
              : Math.max(
                  1,
                  quantity - 1
                );


          input.value =
            quantity;


          if (
            !card.classList.contains(
              'is-selected'
            )
          ) {

            selectCandleCard(
              card
            );

          }


          updateBuilder();

        }
      );


      /* =========================================================
         CLEAR CANDLE CATEGORY
      ========================================================== */

      root.addEventListener(
        'click',
        (event) => {

          const clear =
            event.target.closest(
              '[data-clear-candle-group]'
            );


          if (!clear) return;


          const group =
            clear.dataset.clearCandleGroup;


          candleChoices
            .filter(
              (card) =>
                card.dataset.option ===
                group
            )
            .forEach(
              resetCandleCard
            );


          updateBuilder();

        }
      );


      /* =========================================================
         CARE
      ========================================================== */

      root.addEventListener(
        'click',
        (event) => {

          const toggle =
            event.target.closest(
              '[data-toggle-care]'
            );


          if (
            !toggle ||
            toggle.disabled
          ) {
            return;
          }


          const card =
            toggle.closest(
              '[data-care-choice]'
            );


          if (
            card.classList.contains(
              'is-selected'
            )
          ) {

            resetCareCard(
              card
            );

          } else {

            selectCareCard(
              card
            );

          }


          updateBuilder();

        }
      );


      root.addEventListener(
        'click',
        (event) => {

          const remove =
            event.target.closest(
              '[data-remove-care]'
            );


          if (!remove) return;


          const card =
            remove.closest(
              '[data-care-choice]'
            );


          const kind =
            card?.dataset.careKind;


          resetCareCard(
            card
          );


          updateBuilder();


          showToast(
            kind === 'warmer'
              ? 'Candle Warmer removed.'
              : 'Wick Trimmer removed.'
          );

        }
      );


      root.addEventListener(
        'click',
        (event) => {

          const plus =
            event.target.closest(
              '[data-care-plus]'
            );

          const minus =
            event.target.closest(
              '[data-care-minus]'
            );


          if (
            !plus &&
            !minus
          ) {
            return;
          }


          const card =
            event.target.closest(
              '[data-care-choice]'
            );


          const input =
            card?.querySelector(
              '[data-care-qty]'
            );


          if (!input) return;


          let quantity =
            clampQty(
              input.value
            );


          quantity =
            plus
              ? Math.min(
                  99,
                  quantity + 1
                )
              : Math.max(
                  1,
                  quantity - 1
                );


          input.value =
            quantity;


          if (
            !card.classList.contains(
              'is-selected'
            )
          ) {

            selectCareCard(
              card
            );

          }


          updateBuilder();

        }
      );


      /* =========================================================
         MAIN QUANTITY
      ========================================================== */

      function setMainQuantity(value) {

        const quantity =
          clampQty(
            value
          );


        if (mainQty) {
          mainQty.value =
            quantity;
        }

        if (builderMainQty) {
          builderMainQty.value =
            quantity;
        }


        return quantity;

      }


      function changeMainQty(amount) {

        const quantity =
          clampQty(
            mainQty?.value ||
            builderMainQty?.value ||
            1
          );


        setMainQuantity(
          Math.max(
            1,
            Math.min(
              99,
              quantity + amount
            )
          )
        );


        updateMainPreview();

        updateBuilder();

      }


      mainMinus
        ?.addEventListener(
          'click',
          () => changeMainQty(-1)
        );

      mainPlus
        ?.addEventListener(
          'click',
          () => changeMainQty(1)
        );

      builderMainMinus
        ?.addEventListener(
          'click',
          () => changeMainQty(-1)
        );

      builderMainPlus
        ?.addEventListener(
          'click',
          () => changeMainQty(1)
        );


      mainQty
        ?.addEventListener(
          'input',
          () => {

            setMainQuantity(
              mainQty.value
            );

            updateMainPreview();

            updateBuilder();

          }
        );


      builderMainQty
        ?.addEventListener(
          'input',
          () => {

            setMainQuantity(
              builderMainQty.value
            );

            updateMainPreview();

            updateBuilder();

          }
        );


      variantSelect
        ?.addEventListener(
          'change',
          () => {

            syncMainVariant();

            updateMainPreview();

            updateBuilder();

          }
        );


      /* =========================================================
         PROJECTED SUMMARY
      ========================================================== */

      function projectedSummary(
        incomingCandles = [],
        incomingCare = []
      ) {

        const existing =
          getCartSummary(
            currentCart
          );


        const pendingCandleQty =
          incomingCandles.reduce(
            (
              total,
              item
            ) =>
              total +
              Number(
                item.quantity ||
                0
              ),
            0
          );


        const pendingCandleSubtotal =
          incomingCandles.reduce(
            (
              total,
              item
            ) =>
              total +
              (
                Number(
                  item.price ||
                  0
                ) *
                Number(
                  item.quantity ||
                  0
                )
              ),
            0
          );


        const pendingCareQty =
          incomingCare.reduce(
            (
              total,
              item
            ) =>
              total +
              Number(
                item.quantity ||
                0
              ),
            0
          );


        const pendingCareSubtotal =
          incomingCare.reduce(
            (
              total,
              item
            ) =>
              total +
              (
                Number(
                  item.price ||
                  0
                ) *
                Number(
                  item.quantity ||
                  0
                )
              ),
            0
          );


        const candleQty =
          existing.candleQty +
          pendingCandleQty;


        const candleSubtotal =
          existing.candleOriginalSubtotal +
          pendingCandleSubtotal;


        const careQty =
          existing.careQty +
          pendingCareQty;


        const careSubtotal =
          existing.careSubtotal +
          pendingCareSubtotal;


        const unlocked =
          candleQty >=
          discountMin;


        const saving =
          unlocked
            ? Math.round(
                candleSubtotal *
                (
                  discountPercent /
                  100
                )
              )
            : 0;


        return {
          candleQty,
          candleSubtotal,
          careQty,
          careSubtotal,
          saving,

          total:
            Math.max(
              0,
              candleSubtotal -
              saving
            ) +
            careSubtotal,

          unlocked,

          relevantQty:
            candleQty +
            careQty
        };

      }


      /* =========================================================
         MAIN PREVIEW
      ========================================================== */

      function updateMainPreview() {

        const variant =
          selectedVariant();


        const quantity =
          setMainQuantity(
            mainQty?.value ||
            1
          );


        const projected =
          projectedSummary(
            [
              {
                id:
                  variant.id,

                price:
                  variant.price,

                quantity,

                type:
                  'candle'
              }
            ],
            []
          );


        if (mainPrice) {
          mainPrice.textContent =
            formatMoney(
              variant.price
            );
        }

        if (mobileMainPrice) {
          mobileMainPrice.textContent =
            formatMoney(
              variant.price
            );
        }

        if (mainSubtotal) {
          mainSubtotal.textContent =
            formatMoney(
              variant.price *
              quantity
            );
        }

        if (projectedCount) {
          projectedCount.textContent =
            projected.candleQty;
        }

        if (projectedSaving) {
          projectedSaving.textContent =
            projected.unlocked
              ? `−${formatMoney(
                  projected.saving
                )}`
              : '—';
        }

        if (projectedTotal) {
          projectedTotal.textContent =
            formatMoney(
              projected.total
            );
        }


        offerBox
          ?.classList
          .toggle(
            'is-unlocked',
            projected.unlocked
          );


        if (offerCount) {

          offerCount.textContent =
            `${Math.min(
              projected.candleQty,
              discountMin
            )} / ${discountMin}`;

        }


        if (offerProgress) {

          offerProgress.style.width =
            `${Math.min(
              100,
              (
                projected.candleQty /
                discountMin
              ) *
              100
            )}%`;

        }


        if (offerMessage) {

          const remaining =
            Math.max(
              0,
              discountMin -
              projected.candleQty
            );


          offerMessage.textContent =
            projected.unlocked
              ? `${discountPercent}% candle offer unlocked ✓`
              : (
                  remaining === 1
                    ? `Add 1 more candle to unlock ${discountPercent}% off.`
                    : `Add ${remaining} more candles to unlock ${discountPercent}% off.`
                );

        }


        if (
          mainAdd &&
          !mainAdd.dataset.busy
        ) {

          mainAdd.disabled =
            !variant.available;


          mainAdd.textContent =
            variant.available
              ? (
                  quantity > 1
                    ? `ADD ${quantity} CANDLES`
                    : 'ADD TO CART'
                )
              : 'SOLD OUT';

        }


        if (mainBuy) {

          mainBuy.disabled =
            !variant.available;

        }

      }


      /* =========================================================
         TOTAL ELEMENTS
      ========================================================== */

      const completeSelection =
        root.querySelector(
          '[data-complete-selection]'
        );

      const builderChoiceCount =
        root.querySelector(
          '[data-builder-choice-count]'
        );

      const selectionAvatars =
        root.querySelector(
          '[data-selection-avatars]'
        );

      const selectionList =
        root.querySelector(
          '[data-selection-list]'
        );

      const clearBuilder =
        root.querySelector(
          '[data-clear-builder]'
        );

      const selectionCandleCount =
        root.querySelector(
          '[data-selection-candle-count]'
        );

      const selectionCandleSubtotal =
        root.querySelector(
          '[data-selection-candle-subtotal]'
        );

      const selectionCareSubtotal =
        root.querySelector(
          '[data-selection-care-subtotal]'
        );

      const selectionSaving =
        root.querySelector(
          '[data-selection-saving]'
        );

      const selectionSavingBox =
        root.querySelector(
          '[data-selection-saving-box]'
        );

      const selectionTotal =
        root.querySelector(
          '[data-selection-total]'
        );

      const selectionMessage =
        root.querySelector(
          '[data-selection-message]'
        );

      const selectionProgress =
        root.querySelector(
          '[data-selection-progress]'
        );

      const addCompleteSelection =
        root.querySelector(
          '[data-add-complete-selection]'
        );


      const topTotal =
        root.querySelector(
          '.cce13-top-total'
        );

      const liveCartItems =
        root.querySelector(
          '[data-live-cart-items]'
        );

      const liveCandleCount =
        root.querySelector(
          '[data-live-candle-count]'
        );

      const liveCandleSubtotal =
        root.querySelector(
          '[data-live-candle-subtotal]'
        );

      const liveCareSubtotal =
        root.querySelector(
          '[data-live-care-subtotal]'
        );

      const liveSaving =
        root.querySelector(
          '[data-live-saving]'
        );

      const liveSavingBox =
        root.querySelector(
          '[data-live-saving-box]'
        );

      const liveTotal =
        root.querySelector(
          '[data-live-total]'
        );


      /* =========================================================
         SUMMARY REMOVAL
      ========================================================== */

      function removeBuilderItem(item) {

        if (item.key === 'main') {

          builderMainActive =
            false;

          updateBuilderMainState();

          updateBuilder();

          return;

        }


        if (item.type === 'candle') {

          const card =
            candleChoices.find(
              (candidate) =>
                Number(
                  candidate.dataset.variantId
                ) ===
                  Number(
                    item.id
                  ) &&
                candidate.dataset.option ===
                  item.option
            );


          resetCandleCard(
            card
          );

          updateBuilder();

          return;

        }


        if (item.type === 'care') {

          const card =
            careChoices.find(
              (candidate) =>
                Number(
                  candidate.dataset.variantId
                ) ===
                Number(
                  item.id
                )
            );


          resetCareCard(
            card
          );

          updateBuilder();

        }

      }


      /* =========================================================
         AVATARS
      ========================================================== */

      function renderSelectionAvatars(items) {

        if (!selectionAvatars) return;


        selectionAvatars.innerHTML =
          '';


        items.forEach(
          (item) => {

            const avatar =
              document.createElement(
                'div'
              );


            avatar.className =
              'cce13-avatar';


            if (item.type === 'care') {

              avatar.classList.add(
                'is-care'
              );

            }


            if (item.image) {

              const image =
                document.createElement(
                  'img'
                );


              image.src =
                item.image;

              image.alt =
                item.title;


              avatar.appendChild(
                image
              );

            }


            const qty =
              document.createElement(
                'span'
              );


            qty.textContent =
              item.quantity;


            avatar.appendChild(
              qty
            );


            selectionAvatars.appendChild(
              avatar
            );

          }
        );

      }


      /* =========================================================
         REMOVABLE SELECTION LIST
      ========================================================== */

      function renderSelectionList(items) {

        if (!selectionList) return;


        selectionList.innerHTML =
          '';


        items.forEach(
          (item) => {

            const row =
              document.createElement(
                'article'
              );


            row.className =
              'cce13-summary-item';


            if (item.type === 'care') {

              row.classList.add(
                'is-care'
              );

            }


            const media =
              document.createElement(
                'div'
              );


            media.className =
              'cce13-summary-item__image';


            if (item.image) {

              const image =
                document.createElement(
                  'img'
                );


              image.src =
                item.image;

              image.alt =
                item.title;


              media.appendChild(
                image
              );

            }


            const content =
              document.createElement(
                'div'
              );


            const type =
              document.createElement(
                'span'
              );


            type.className =
              'cce13-summary-item__type';


            if (item.key === 'main') {

              type.textContent =
                '13OZ METAL CANDLE';

            } else if (item.type === 'care') {

              type.textContent =
                item.kind === 'warmer'
                  ? 'CANDLE WARMER'
                  : 'WICK TRIMMER';

            } else {

              type.textContent =
                'CANDLE';

            }


            const title =
              document.createElement(
                'strong'
              );


            title.className =
              'cce13-summary-item__title';


            title.textContent =
              item.title;


            const meta =
              document.createElement(
                'span'
              );


            meta.className =
              'cce13-summary-item__meta';


            meta.textContent =
              `Qty ${item.quantity} · ${formatMoney(
                item.price *
                item.quantity
              )}`;


            content.appendChild(
              type
            );

            content.appendChild(
              title
            );

            content.appendChild(
              meta
            );


            const remove =
              document.createElement(
                'button'
              );


            remove.type =
              'button';

            remove.className =
              'cce13-summary-item__remove';

            remove.textContent =
              'REMOVE';


            remove.addEventListener(
              'click',
              () => {

                removeBuilderItem(
                  item
                );


                showToast(
                  `${item.title} removed.`
                );

              }
            );


            row.appendChild(
              media
            );

            row.appendChild(
              content
            );

            row.appendChild(
              remove
            );


            selectionList.appendChild(
              row
            );

          }
        );

      }


      /* =========================================================
         UPDATE BUILDER
      ========================================================== */

      function updateBuilder() {

        const candles =
          selectedCandles();

        const care =
          selectedCare();

        const selectedItems = [
          ...candles,
          ...care
        ];


        const projected =
          projectedSummary(
            candles,
            care
          );


        const cartSummary =
          getCartSummary(
            currentCart
          );


        const selectedUnits =
          selectedItems.reduce(
            (
              total,
              item
            ) =>
              total +
              Number(
                item.quantity ||
                0
              ),
            0
          );


        if (builderChoiceCount) {
          builderChoiceCount.textContent =
            selectedItems.length;
        }


        /* BOTTOM TOTAL */

        if (selectionCandleCount) {
          selectionCandleCount.textContent =
            projected.candleQty;
        }

        if (selectionCandleSubtotal) {
          selectionCandleSubtotal.textContent =
            formatMoney(
              projected.candleSubtotal
            );
        }

        if (selectionCareSubtotal) {
          selectionCareSubtotal.textContent =
            projected.careSubtotal > 0
              ? formatMoney(
                  projected.careSubtotal
                )
              : '—';
        }

        if (selectionSaving) {
          selectionSaving.textContent =
            projected.unlocked
              ? `−${formatMoney(
                  projected.saving
                )}`
              : '—';
        }

        selectionSavingBox
          ?.classList
          .toggle(
            'is-active',
            projected.unlocked
          );

        if (selectionTotal) {
          selectionTotal.textContent =
            formatMoney(
              projected.total
            );
        }


        /* TOP TOTAL */

        if (liveCartItems) {
          liveCartItems.textContent =
            cartSummary.relevantQty;
        }

        if (liveCandleCount) {
          liveCandleCount.textContent =
            projected.candleQty;
        }

        if (liveCandleSubtotal) {
          liveCandleSubtotal.textContent =
            formatMoney(
              projected.candleSubtotal
            );
        }

        if (liveCareSubtotal) {
          liveCareSubtotal.textContent =
            projected.careSubtotal > 0
              ? formatMoney(
                  projected.careSubtotal
                )
              : '—';
        }

        if (liveSaving) {
          liveSaving.textContent =
            projected.unlocked
              ? `−${formatMoney(
                  projected.saving
                )}`
              : '—';
        }

        liveSavingBox
          ?.classList
          .toggle(
            'is-active',
            projected.unlocked
          );

        if (liveTotal) {
          liveTotal.textContent =
            formatMoney(
              projected.total
            );
        }


        /* OFFER */

        if (selectionProgress) {

          selectionProgress.style.width =
            `${Math.min(
              100,
              (
                projected.candleQty /
                discountMin
              ) *
              100
            )}%`;

        }


        if (selectionMessage) {

          const remaining =
            Math.max(
              0,
              discountMin -
              projected.candleQty
            );


          selectionMessage.textContent =
            projected.unlocked
              ? `${discountPercent}% candle offer unlocked ✓`
              : (
                  remaining === 1
                    ? `Add 1 more candle to unlock ${discountPercent}% off.`
                    : `Add ${remaining} more candles to unlock ${discountPercent}% off.`
                );

        }


        if (
          addCompleteSelection &&
          !addCompleteSelection.dataset.busy
        ) {

          addCompleteSelection.disabled =
            selectedUnits === 0;


          addCompleteSelection.classList.toggle(
            'is-unlocked',
            projected.unlocked
          );


          if (selectedUnits === 0) {

            addCompleteSelection.textContent =
              'SELECT AN ITEM';

          } else if (projected.unlocked) {

            addCompleteSelection.textContent =
              `ADD ${selectedUnits} ITEM${selectedUnits === 1 ? '' : 'S'} · ${discountPercent}% CANDLE OFFER`;

          } else {

            addCompleteSelection.textContent =
              `ADD ${selectedUnits} ITEM${selectedUnits === 1 ? '' : 'S'} TO CART`;

          }

        }


        renderSelectionAvatars(
          selectedItems
        );


        renderSelectionList(
          selectedItems
        );


        animateUpdate(
          completeSelection
        );


        animateUpdate(
          topTotal
        );

      }


      /* =========================================================
         REMOVE OPTIONAL SELECTIONS
      ========================================================== */

      clearBuilder
        ?.addEventListener(
          'click',
          () => {

            candleChoices.forEach(
              resetCandleCard
            );


            careChoices.forEach(
              resetCareCard
            );


            updateBuilder();


            showToast(
              'Optional items removed.'
            );

          }
        );


      /* =========================================================
         CONFIRMED CART
      ========================================================== */

      const pageCart =
        root.querySelector(
          '[data-page-cart]'
        );

      const cartItems =
        root.querySelector(
          '[data-cart-items]'
        );

      const cartItemCount =
        root.querySelector(
          '[data-cart-item-count]'
        );

      const cartRelevantCount =
        root.querySelector(
          '[data-cart-relevant-count]'
        );

      const cartCandleSubtotal =
        root.querySelector(
          '[data-cart-candle-subtotal]'
        );

      const cartCareSubtotal =
        root.querySelector(
          '[data-cart-care-subtotal]'
        );

      const cartSaving =
        root.querySelector(
          '[data-cart-saving]'
        );

      const cartSavingBox =
        root.querySelector(
          '[data-cart-saving-box]'
        );

      const cartTotal =
        root.querySelector(
          '[data-cart-total]'
        );

      const cartOfferMessage =
        root.querySelector(
          '[data-cart-offer-message]'
        );

      const cartProgress =
        root.querySelector(
          '[data-cart-progress]'
        );

      const cartCheckout =
        root.querySelector(
          '[data-cart-checkout]'
        );


      function renderCartNumbers(summary) {

        if (cartItemCount) {
          cartItemCount.textContent =
            summary.relevantQty;
        }

        if (cartRelevantCount) {
          cartRelevantCount.textContent =
            summary.relevantQty;
        }

        if (cartCandleSubtotal) {
          cartCandleSubtotal.textContent =
            formatMoney(
              summary.candleOriginalSubtotal
            );
        }

        if (cartCareSubtotal) {
          cartCareSubtotal.textContent =
            summary.careSubtotal > 0
              ? formatMoney(
                  summary.careSubtotal
                )
              : '—';
        }

        if (cartSaving) {
          cartSaving.textContent =
            summary.unlocked
              ? `−${formatMoney(
                  summary.displaySaving
                )}`
              : '—';
        }

        cartSavingBox
          ?.classList
          .toggle(
            'is-active',
            summary.unlocked
          );

        if (cartTotal) {
          cartTotal.textContent =
            formatMoney(
              summary.total
            );
        }


        if (cartProgress) {

          cartProgress.style.width =
            `${Math.min(
              100,
              (
                summary.candleQty /
                discountMin
              ) *
              100
            )}%`;

        }


        if (cartOfferMessage) {

          const remaining =
            Math.max(
              0,
              discountMin -
              summary.candleQty
            );


          cartOfferMessage.textContent =
            summary.unlocked
              ? `${discountPercent}% candle offer unlocked ✓`
              : (
                  remaining === 1
                    ? `Add 1 more candle to unlock ${discountPercent}% off.`
                    : `Add ${remaining} more candles to unlock ${discountPercent}% off.`
                );

        }


        if (cartCheckout) {

          cartCheckout.classList.toggle(
            'is-unlocked',
            summary.unlocked
          );


          cartCheckout.href =
            (
              summary.unlocked &&
              discountCode
            )
              ? `${shopRoot()}discount/${encodeURIComponent(
                  discountCode
                )}?redirect=${encodeURIComponent(
                  checkoutUrl
                )}`
              : checkoutUrl;

        }

      }


      function createCartItem(item) {

        const article =
          document.createElement(
            'article'
          );


        article.className =
          'cce13-cart-item';


        if (item.cceType === 'care') {

          article.classList.add(
            'is-care'
          );

        }


        const media =
          document.createElement(
            'div'
          );


        media.className =
          'cce13-cart-item__image';


        if (item.image) {

          const image =
            document.createElement(
              'img'
            );


          image.src =
            item.image;

          image.alt =
            item.product_title ||
            item.title ||
            'Cart item';


          media.appendChild(
            image
          );

        }


        const badge =
          document.createElement(
            'span'
          );


        badge.className =
          'cce13-cart-item__badge';

        badge.textContent =
          item.quantity;


        media.appendChild(
          badge
        );


        const content =
          document.createElement(
            'div'
          );


        const title =
          document.createElement(
            'div'
          );


        title.className =
          'cce13-cart-item__title';

        title.textContent =
          item.product_title ||
          item.title ||
          'Cart item';


        const type =
          document.createElement(
            'span'
          );


        type.className =
          'cce13-cart-item__type';

        type.textContent =
          item.cceType === 'care'
            ? 'CANDLE CARE'
            : 'CANDLE';


        const price =
          document.createElement(
            'strong'
          );


        price.className =
          'cce13-cart-item__price';

        price.textContent =
          formatMoney(
            item.cceFinalLine ||
            item.final_line_price ||
            item.line_price ||
            0
          );


        const controls =
          document.createElement(
            'div'
          );


        controls.className =
          'cce13-cart-item__controls';


        const minus =
          document.createElement(
            'button'
          );

        minus.type =
          'button';

        minus.textContent =
          '−';


        const qty =
          document.createElement(
            'span'
          );

        qty.textContent =
          item.quantity;


        const plus =
          document.createElement(
            'button'
          );

        plus.type =
          'button';

        plus.textContent =
          '+';


        const remove =
          document.createElement(
            'button'
          );

        remove.type =
          'button';

        remove.className =
          'cce13-cart-item__remove';

        remove.textContent =
          'REMOVE';


        async function updateLine(
          quantity
        ) {

          if (syncingCart) return;


          syncingCart =
            true;


          article.style.opacity =
            '.55';


          try {

            let cart =
              await changeCartLine(
                item.key,
                quantity
              );


            cart =
              await reconcileChangedCart(
                cart
              );


            currentCart =
              cart;


            renderPageCart(
              cart
            );


            broadcastConfirmedCart(
              cart
            );


            showToast(
              quantity === 0
                ? 'Item removed from cart.'
                : 'Cart updated.'
            );


          } catch (error) {

            article.style.opacity =
              '1';


            showToast(
              error.message
            );


          } finally {

            syncingCart =
              false;

          }

        }


        minus.addEventListener(
          'click',
          () => {

            updateLine(
              Math.max(
                0,
                item.quantity - 1
              )
            );

          }
        );


        plus.addEventListener(
          'click',
          () => {

            updateLine(
              item.quantity + 1
            );

          }
        );


        remove.addEventListener(
          'click',
          () => {

            updateLine(
              0
            );

          }
        );


        controls.appendChild(
          minus
        );

        controls.appendChild(
          qty
        );

        controls.appendChild(
          plus
        );

        controls.appendChild(
          remove
        );


        content.appendChild(
          title
        );

        content.appendChild(
          type
        );

        content.appendChild(
          price
        );

        content.appendChild(
          controls
        );


        article.appendChild(
          media
        );

        article.appendChild(
          content
        );


        return article;

      }


      function renderPageCart(cart) {

        currentCart =
          cart;


        const summary =
          getCartSummary(
            cart
          );


        if (!pageCart) return;


        pageCart.hidden =
          summary.relevantQty === 0;


        if (cartItems) {

          cartItems.innerHTML =
            '';


          summary.items.forEach(
            (item) => {

              cartItems.appendChild(
                createCartItem(
                  item
                )
              );

            }
          );

        }


        renderCartNumbers(
          summary
        );


        updateMainPreview();

        updateBuilder();


        animateUpdate(
          pageCart
        );

      }


      /* =========================================================
         FAST PREVIEW
      ========================================================== */

      function renderFastCartPreview(items) {

        const candles =
          items.filter(
            (item) =>
              item.type === 'candle'
          );


        const care =
          items.filter(
            (item) =>
              item.type === 'care'
          );


        const projected =
          projectedSummary(
            candles,
            care
          );


        if (pageCart) {

          pageCart.hidden =
            false;

        }


        optimisticHeaderCount(
          items.reduce(
            (
              total,
              item
            ) =>
              total +
              item.quantity,
            0
          )
        );


        return projected;

      }


      /* =========================================================
         MAIN ADD
      ========================================================== */

      mainForm
        ?.addEventListener(
          'submit',
          async (event) => {

            event.preventDefault();


            if (syncingCart) return;


            const variant =
              selectedVariant();


            const quantity =
              clampQty(
                mainQty?.value ||
                1
              );


            if (
              !variant.id ||
              !variant.available
            ) {
              return;
            }


            const incoming = [{
              type: 'candle',
              id: variant.id,
              price: variant.price,
              quantity,

              title:
                root
                  .querySelector(
                    '.cce13-mobile-heading h1'
                  )
                  ?.textContent
                  ?.trim() ||
                '13oz Metal Candle',

              image:
                variant.image ||
                root
                  .querySelector(
                    '.cce13-main-choice__img'
                  )
                  ?.src ||
                ''
            }];


            const projected =
              renderFastCartPreview(
                incoming
              );


            syncingCart =
              true;


            mainAdd.disabled =
              true;

            mainAdd.dataset.busy =
              'true';

            mainAdd.textContent =
              'ADDING…';


            try {

              await addLinesToShopify(
                [{
                  id:
                    variant.id,

                  quantity
                }]
              );


              const cart =
                await reconcileAfterAdd(
                  projected.unlocked
                );


              currentCart =
                cart;


              renderPageCart(
                cart
              );


              broadcastConfirmedCart(
                cart
              );


              const confirmed =
                getCartSummary(
                  cart
                );


              mainAdd.classList.add(
                'is-success'
              );


              mainAdd.textContent =
                confirmed.unlocked
                  ? `ADDED · ${discountPercent}% UNLOCKED ✓`
                  : 'ADDED ✓';


              showToast(
                `${quantity} candle${quantity === 1 ? '' : 's'} added to cart.`
              );


              window.setTimeout(
                () => {

                  delete mainAdd.dataset.busy;


                  mainAdd.classList.remove(
                    'is-success'
                  );


                  mainAdd.disabled =
                    false;


                  updateMainPreview();

                },
                700
              );


            } catch (error) {

              delete mainAdd.dataset.busy;


              mainAdd.disabled =
                false;


              if (currentCart) {

                renderPageCart(
                  currentCart
                );

                updateThemeCartCount(
                  currentCart.item_count
                );

              }


              showToast(
                error.message
              );


            } finally {

              syncingCart =
                false;

            }

          }
        );


      /* =========================================================
         BUY NOW
      ========================================================== */

      mainBuy
        ?.addEventListener(
          'click',
          async () => {

            if (syncingCart) return;


            const variant =
              selectedVariant();


            const quantity =
              clampQty(
                mainQty?.value ||
                1
              );


            if (
              !variant.id ||
              !variant.available
            ) {
              return;
            }


            const summary =
              getCartSummary(
                currentCart
              );


            const unlocks =
              (
                summary.candleQty +
                quantity
              ) >=
              discountMin;


            syncingCart =
              true;


            mainBuy.disabled =
              true;

            mainBuy.textContent =
              'PREPARING…';


            try {

              await addLinesToShopify(
                [{
                  id:
                    variant.id,

                  quantity
                }]
              );


              window.location.href =
                (
                  unlocks &&
                  discountCode
                )
                  ? `${shopRoot()}discount/${encodeURIComponent(
                      discountCode
                    )}?redirect=${encodeURIComponent(
                      checkoutUrl
                    )}`
                  : checkoutUrl;


            } catch (error) {

              syncingCart =
                false;


              mainBuy.disabled =
                false;

              mainBuy.textContent =
                'BUY NOW';


              showToast(
                error.message
              );

            }

          }
        );


      /* =========================================================
         COMPLETE SELECTION ADD
      ========================================================== */

      function resetBuilderAfterAdd() {

        builderMainActive =
          false;


        updateBuilderMainState();


        candleChoices.forEach(
          resetCandleCard
        );


        careChoices.forEach(
          resetCareCard
        );


        setMainQuantity(
          1
        );

      }


      addCompleteSelection
        ?.addEventListener(
          'click',
          async () => {

            if (
              syncingCart ||
              addCompleteSelection.disabled
            ) {
              return;
            }


            const candles =
              selectedCandles();

            const care =
              selectedCare();


            const allItems = [
              ...candles,
              ...care
            ];


            if (!allItems.length) {

              showToast(
                'Choose at least one item.'
              );

              return;

            }


            const projected =
              renderFastCartPreview(
                allItems
              );


            const totalUnits =
              allItems.reduce(
                (
                  total,
                  item
                ) =>
                  total +
                  item.quantity,
                0
              );


            syncingCart =
              true;


            addCompleteSelection.disabled =
              true;

            addCompleteSelection.dataset.busy =
              'true';

            addCompleteSelection.textContent =
              'ADDING EVERYTHING…';


            try {

              await addLinesToShopify(
                allItems.map(
                  (item) => ({
                    id:
                      item.id,

                    quantity:
                      item.quantity
                  })
                )
              );


              const cart =
                await reconcileAfterAdd(
                  projected.unlocked
                );


              currentCart =
                cart;


              resetBuilderAfterAdd();


              renderPageCart(
                cart
              );


              broadcastConfirmedCart(
                cart
              );


              addCompleteSelection.classList.add(
                'is-success'
              );


              addCompleteSelection.textContent =
                'EVERYTHING ADDED ✓';


              showToast(
                `${totalUnits} item${totalUnits === 1 ? '' : 's'} added to the same cart.`
              );


              window.setTimeout(
                () => {

                  delete addCompleteSelection.dataset.busy;


                  addCompleteSelection.classList.remove(
                    'is-success'
                  );


                  updateBuilder();

                },
                750
              );


            } catch (error) {

              delete addCompleteSelection.dataset.busy;


              addCompleteSelection.disabled =
                false;


              if (currentCart) {

                renderPageCart(
                  currentCart
                );

                updateThemeCartCount(
                  currentCart.item_count
                );

              }


              showToast(
                error.message
              );


            } finally {

              syncingCart =
                false;

            }

          }
        );


      /* =========================================================
         SEARCH
      ========================================================== */

      const groups = [
        'glass15',
        'porcelain',
        'metal11'
      ];


      function cardsForGroup(group) {

        return candleChoices.filter(
          (card) =>
            card.dataset.option ===
            group
        );

      }


      function visibleCardsForGroup(group) {

        return cardsForGroup(
          group
        ).filter(
          (card) =>
            !card.hidden
        );

      }


      function filterGroup(group) {

        const input =
          root.querySelector(
            `[data-candle-search="${group}"]`
          );


        const clear =
          root.querySelector(
            `[data-candle-search-clear="${group}"]`
          );


        const count =
          root.querySelector(
            `[data-candle-search-count="${group}"]`
          );


        const noResults =
          root.querySelector(
            `[data-candle-no-results="${group}"]`
          );


        const rail =
          root.querySelector(
            `[data-candle-rail="${group}"]`
          );


        const query =
          normalize(
            input?.value ||
            ''
          );


        let visible =
          0;


        cardsForGroup(
          group
        ).forEach(
          (card) => {

            const text =
              normalize(
                card.dataset.search ||
                card.dataset.title ||
                card.textContent
              );


            const matches =
              !query ||
              text.includes(
                query
              );


            card.hidden =
              !matches;


            if (matches) {
              visible += 1;
            }

          }
        );


        if (clear) {
          clear.hidden =
            !query;
        }


        if (count) {

          count.textContent =
            `${visible} ${
              visible === 1
                ? 'choice'
                : 'choices'
            }`;

        }


        if (noResults) {

          noResults.hidden =
            visible > 0;

        }


        rail?.scrollTo({
          left: 0,
          behavior: 'auto'
        });


        requestAnimationFrame(
          () => {

            buildChoiceDots(
              group
            );

          }
        );

      }


      groups.forEach(
        (group) => {

          const input =
            root.querySelector(
              `[data-candle-search="${group}"]`
            );


          const clear =
            root.querySelector(
              `[data-candle-search-clear="${group}"]`
            );


          input
            ?.addEventListener(
              'input',
              () => {

                filterGroup(
                  group
                );

              }
            );


          clear
            ?.addEventListener(
              'click',
              () => {

                if (input) {
                  input.value =
                    '';
                }

                filterGroup(
                  group
                );

                input?.focus();

              }
            );

        }
      );


      /* =========================================================
         CHOICE PAGINATION
      ========================================================== */

      function buildChoiceDots(group) {

        const rail =
          root.querySelector(
            `[data-candle-rail="${group}"]`
          );


        const dots =
          root.querySelector(
            `[data-candle-dots="${group}"]`
          );


        if (
          !rail ||
          !dots
        ) {
          return;
        }


        const cards =
          visibleCardsForGroup(
            group
          );


        dots.innerHTML =
          '';


        if (!cards.length) {
          return;
        }


        const cardWidth =
          cards[0]
            .getBoundingClientRect()
            .width ||
          1;


        const style =
          window.getComputedStyle(
            rail
          );


        const gap =
          parseFloat(
            style.gap ||
            '8'
          ) ||
          8;


        const perPage =
          Math.max(
            1,
            Math.floor(
              (
                rail.clientWidth +
                gap
              ) /
              (
                cardWidth +
                gap
              )
            )
          );


        const pages =
          Math.max(
            1,
            Math.ceil(
              cards.length /
              perPage
            )
          );


        for (
          let page = 0;
          page < pages;
          page += 1
        ) {

          const dot =
            document.createElement(
              'button'
            );


          dot.type =
            'button';


          dot.setAttribute(
            'aria-label',
            `View candle page ${page + 1}`
          );


          if (page === 0) {

            dot.classList.add(
              'is-active'
            );

          }


          dot.addEventListener(
            'click',
            () => {

              const target =
                cards[
                  page *
                  perPage
                ];


              if (!target) return;


              rail.scrollTo({
                left:
                  target.offsetLeft -
                  rail.offsetLeft,

                behavior:
                  'smooth'
              });

            }
          );


          dots.appendChild(
            dot
          );

        }

      }


      function updateChoiceDots(group) {

        const rail =
          root.querySelector(
            `[data-candle-rail="${group}"]`
          );


        const dots =
          Array.from(
            root.querySelectorAll(
              `[data-candle-dots="${group}"] button`
            )
          );


        if (
          !rail ||
          !dots.length
        ) {
          return;
        }


        const maxScroll =
          Math.max(
            1,
            rail.scrollWidth -
            rail.clientWidth
          );


        const ratio =
          Math.max(
            0,
            Math.min(
              1,
              rail.scrollLeft /
              maxScroll
            )
          );


        const index =
          Math.round(
            ratio *
            (
              dots.length -
              1
            )
          );


        dots.forEach(
          (dot,dotIndex) => {

            dot.classList.toggle(
              'is-active',
              dotIndex ===
              index
            );

          }
        );

      }


      groups.forEach(
        (group) => {

          const rail =
            root.querySelector(
              `[data-candle-rail="${group}"]`
            );


          root
            .querySelector(
              `[data-candle-prev="${group}"]`
            )
            ?.addEventListener(
              'click',
              () => {

                rail?.scrollBy({
                  left:
                    -Math.max(
                      250,
                      rail.clientWidth *
                      .82
                    ),

                  behavior:
                    'smooth'
                });

              }
            );


          root
            .querySelector(
              `[data-candle-next="${group}"]`
            )
            ?.addEventListener(
              'click',
              () => {

                rail?.scrollBy({
                  left:
                    Math.max(
                      250,
                      rail.clientWidth *
                      .82
                    ),

                  behavior:
                    'smooth'
                });

              }
            );


          let timer;


          rail
            ?.addEventListener(
              'scroll',
              () => {

                window.clearTimeout(
                  timer
                );


                timer =
                  window.setTimeout(
                    () => {

                      updateChoiceDots(
                        group
                      );

                    },
                    50
                  );

              },
              {
                passive: true
              }
            );

        }
      );


      /* =========================================================
         GALLERY
      ========================================================== */

      const galleryRail =
        root.querySelector(
          '[data-gallery-rail]'
        );

      const gallerySlides =
        Array.from(
          root.querySelectorAll(
            '[data-gallery-slide]'
          )
        );

      const galleryDots =
        root.querySelector(
          '[data-gallery-dots]'
        );

      const galleryThumbs =
        Array.from(
          root.querySelectorAll(
            '[data-gallery-thumb]'
          )
        );

      let galleryIndex =
        0;


      function updateGalleryUI() {

        galleryThumbs.forEach(
          (thumb,index) => {

            thumb.classList.toggle(
              'is-active',
              index ===
              galleryIndex
            );

          }
        );


        galleryDots
          ?.querySelectorAll(
            'button'
          )
          .forEach(
            (dot,index) => {

              dot.classList.toggle(
                'is-active',
                index ===
                galleryIndex
              );

            }
          );

      }


      function goToGallery(index) {

        if (
          !galleryRail ||
          !gallerySlides.length
        ) {
          return;
        }


        galleryIndex =
          Math.max(
            0,
            Math.min(
              gallerySlides.length -
              1,
              index
            )
          );


        galleryRail.scrollTo({
          left:
            gallerySlides[
              galleryIndex
            ].offsetLeft,

          behavior:
            'smooth'
        });


        updateGalleryUI();

      }


      function buildGalleryDots() {

        if (
          !galleryDots ||
          gallerySlides.length < 2
        ) {
          return;
        }


        galleryDots.innerHTML =
          '';


        gallerySlides.forEach(
          (_,index) => {

            const dot =
              document.createElement(
                'button'
              );


            dot.type =
              'button';


            dot.setAttribute(
              'aria-label',
              `View image ${index + 1}`
            );


            if (index === 0) {

              dot.classList.add(
                'is-active'
              );

            }


            dot.addEventListener(
              'click',
              () => {

                goToGallery(
                  index
                );

              }
            );


            galleryDots.appendChild(
              dot
            );

          }
        );

      }


      galleryThumbs.forEach(
        (thumb) => {

          thumb.addEventListener(
            'click',
            () => {

              goToGallery(
                Number(
                  thumb.dataset.galleryThumb
                )
              );

            }
          );

        }
      );


      root
        .querySelector(
          '[data-gallery-prev]'
        )
        ?.addEventListener(
          'click',
          () => {

            goToGallery(
              galleryIndex - 1
            );

          }
        );


      root
        .querySelector(
          '[data-gallery-next]'
        )
        ?.addEventListener(
          'click',
          () => {

            goToGallery(
              galleryIndex + 1
            );

          }
        );


      /* =========================================================
         RESIZE
      ========================================================== */

      let resizeTimer;


      window.addEventListener(
        'resize',
        () => {

          window.clearTimeout(
            resizeTimer
          );


          resizeTimer =
            window.setTimeout(
              () => {

                groups.forEach(
                  buildChoiceDots
                );

              },
              120
            );

        }
      );


      /* =========================================================
         INITIALIZE
      ========================================================== */

      function initialize() {

        buildGalleryDots();


        groups.forEach(
          (group) => {

            filterGroup(
              group
            );


            buildChoiceDots(
              group
            );

          }
        );


        updateBuilderMainState();

        syncMainVariant();

        updateMainPreview();

        updateBuilder();


        fetchCart()
          .then(
            (cart) => {

              currentCart =
                cart;


              renderPageCart(
                cart
              );


              broadcastConfirmedCart(
                cart
              );

            }
          )
          .catch(
            (error) => {

              console.warn(
                '[CCE13 initial cart]',
                error
              );

            }
          );

      }


      initialize();

    });

})();